import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { App } from "./App";
import { AppShell } from "./AppShell";
import { RouteErrorScreen } from "./screens/RouteErrorScreen";
import { ProtectedRoute } from "./features/auth/ProtectedRoute";
import { RequireWedding } from "./features/auth/RequireWedding";
import { AdminRoute } from "./features/admin/AdminRoute";
import { lazyNamed } from "./lib/lazyNamed";

const Landing = lazyNamed(() => import("./screens/Landing"), "Landing");
const Login = lazyNamed(() => import("./features/auth/Login"), "Login");
const Register = lazyNamed(() => import("./features/auth/Register"), "Register");
const PasswordReset = lazyNamed(() => import("./features/auth/PasswordReset"), "PasswordReset");
const PasswordResetConfirm = lazyNamed(
  () => import("./features/auth/PasswordResetConfirm"),
  "PasswordResetConfirm",
);
const EmailVerify = lazyNamed(() => import("./features/auth/EmailVerify"), "EmailVerify");
const Onboarding = lazyNamed(() => import("./screens/Onboarding"), "Onboarding");
const AcceptInvite = lazyNamed(() => import("./screens/AcceptInvite"), "AcceptInvite");
const Rsvp = lazyNamed(() => import("./screens/Rsvp"), "Rsvp");
const PublicSeating = lazyNamed(() => import("./screens/PublicSeating"), "PublicSeating");
const GuestUpload = lazyNamed(() => import("./screens/GuestUpload"), "GuestUpload");
const Dashboard = lazyNamed(() => import("./screens/Dashboard"), "Dashboard");
const Guests = lazyNamed(() => import("./screens/Guests"), "Guests");
const Seating = lazyNamed(() => import("./screens/Seating"), "Seating");
const Tasks = lazyNamed(() => import("./screens/Tasks"), "Tasks");
const Budget = lazyNamed(() => import("./screens/Budget"), "Budget");
const VendorsPrint = lazyNamed(() => import("./screens/VendorsPrint"), "VendorsPrint");
const BudgetPrint = lazyNamed(() => import("./screens/BudgetPrint"), "BudgetPrint");
const TasksPrint = lazyNamed(() => import("./screens/TasksPrint"), "TasksPrint");
const AccommodationPrint = lazyNamed(
  () => import("./screens/AccommodationPrint"),
  "AccommodationPrint",
);
const Accommodation = lazyNamed(() => import("./screens/Accommodation"), "Accommodation");
const DayPlan = lazyNamed(() => import("./screens/DayPlan"), "DayPlan");
const Papeteria = lazyNamed(() => import("./screens/Papeteria"), "Papeteria");
const DayPlanPrint = lazyNamed(() => import("./screens/DayPlanPrint"), "DayPlanPrint");
const Website = lazyNamed(() => import("./screens/Website"), "Website");
const PublicSite = lazyNamed(() => import("./screens/PublicSite"), "PublicSite");
const PublicGallery = lazyNamed(() => import("./screens/PublicGallery"), "PublicGallery");
const AIScreen = lazyNamed(() => import("./screens/AIScreen"), "AIScreen");
const Mobile = lazyNamed(() => import("./screens/Mobile"), "Mobile");
const Settings = lazyNamed(() => import("./screens/Settings"), "Settings");
const Upgrade = lazyNamed(() => import("./screens/Upgrade"), "Upgrade");
const AdminLayout = lazyNamed(() => import("./screens/admin/AdminLayout"), "AdminLayout");
const AdminOverview = lazyNamed(() => import("./screens/admin/AdminOverview"), "AdminOverview");
const AdminUsers = lazyNamed(() => import("./screens/admin/AdminUsers"), "AdminUsers");
const AdminUserDetail = lazyNamed(
  () => import("./screens/admin/AdminUserDetail"),
  "AdminUserDetail",
);
const AdminWeddings = lazyNamed(() => import("./screens/admin/AdminWeddings"), "AdminWeddings");
const AdminWeddingDetail = lazyNamed(
  () => import("./screens/admin/AdminWeddingDetail"),
  "AdminWeddingDetail",
);
const AdminAiLogs = lazyNamed(() => import("./screens/admin/AdminAiLogs"), "AdminAiLogs");
const AdminFeedback = lazyNamed(() => import("./screens/admin/AdminFeedback"), "AdminFeedback");
const AdminFeatureFlags = lazyNamed(
  () => import("./screens/admin/AdminFeatureFlags"),
  "AdminFeatureFlags",
);
const AdminAnnouncements = lazyNamed(
  () => import("./screens/admin/AdminAnnouncements"),
  "AdminAnnouncements",
);
const About = lazyNamed(() => import("./screens/About"), "About");
const Blog = lazyNamed(() => import("./screens/Blog"), "Blog");
const BlogPost = lazyNamed(() => import("./screens/BlogPost"), "BlogPost");
const Contact = lazyNamed(() => import("./screens/Contact"), "Contact");
const SalutationGenerator = lazyNamed(
  () => import("./screens/SalutationGenerator"),
  "SalutationGenerator",
);
const LegalPage = lazyNamed(() => import("./screens/LegalPage"), "LegalPage");
const NotFound = lazyNamed(() => import("./screens/NotFound"), "NotFound");

export const router = createBrowserRouter([
  {
    element: <App />,
    errorElement: <RouteErrorScreen />,
    children: [
      { path: "/", element: <Landing /> },
      { path: "/en", element: <Landing /> },
      { path: "/de", element: <Landing /> },
      { path: "/login", element: <Login /> },
      { path: "/register", element: <Register /> },
      { path: "/reset", element: <PasswordReset /> },
      { path: "/reset/confirm", element: <PasswordResetConfirm /> },
      { path: "/verify-email", element: <EmailVerify /> },
      {
        path: "/onboarding",
        element: (
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        ),
      },
      {
        path: "/app/budget/vendors/print",
        element: (
          <ProtectedRoute>
            <VendorsPrint />
          </ProtectedRoute>
        ),
      },
      {
        path: "/app/budget/print",
        element: (
          <ProtectedRoute>
            <BudgetPrint />
          </ProtectedRoute>
        ),
      },
      {
        path: "/app/tasks/print",
        element: (
          <ProtectedRoute>
            <TasksPrint />
          </ProtectedRoute>
        ),
      },
      {
        path: "/app/accommodation/print",
        element: (
          <ProtectedRoute>
            <AccommodationPrint />
          </ProtectedRoute>
        ),
      },
      {
        path: "/app/day-plan/print",
        element: (
          <ProtectedRoute>
            <DayPlanPrint />
          </ProtectedRoute>
        ),
      },
      {
        path: "/app",
        element: (
          <ProtectedRoute>
            <RequireWedding>
              <AppShell>
                <Outlet />
              </AppShell>
            </RequireWedding>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: "dashboard", element: <Dashboard /> },
          { path: "guests", element: <Guests /> },
          { path: "seating", element: <Seating /> },
          { path: "tasks", element: <Tasks /> },
          { path: "budget", element: <Budget /> },
          { path: "accommodation", element: <Accommodation /> },
          { path: "day-plan", element: <DayPlan /> },
          { path: "papeteria", element: <Papeteria /> },
          { path: "website", element: <Website /> },
          { path: "ai", element: <AIScreen /> },
          { path: "settings", element: <Settings /> },
          { path: "upgrade", element: <Upgrade /> },
          {
            path: "admin",
            element: (
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            ),
            children: [
              { index: true, element: <AdminOverview /> },
              { path: "users", element: <AdminUsers /> },
              { path: "users/:userId", element: <AdminUserDetail /> },
              { path: "weddings", element: <AdminWeddings /> },
              { path: "weddings/:weddingId", element: <AdminWeddingDetail /> },
              { path: "ai-logs", element: <AdminAiLogs /> },
              { path: "feedback", element: <AdminFeedback /> },
              { path: "feature-flags", element: <AdminFeatureFlags /> },
              { path: "announcements", element: <AdminAnnouncements /> },
            ],
          },
        ],
      },
      // Public guest-facing wedding site — no auth, served from the
      // `api` Cloud Function via usePublicSite.
      { path: "/w/:handle", element: <PublicSite /> },
      // Dedicated full-page photo gallery (parallax hero + lightbox).
      { path: "/w/:handle/galeria", element: <PublicGallery /> },
      { path: "/w/:handle/gallery", element: <PublicGallery /> },
      // Public, unauthenticated per-guest RSVP form.
      { path: "/rsvp/:token", element: <Rsvp /> },
      // Public, unauthenticated fullscreen seating-plan viewer.
      { path: "/seating/:token", element: <PublicSeating /> },
      // Public, unauthenticated guest-photo upload page (QR target).
      { path: "/upload/:token", element: <GuestUpload /> },
      // Public partner-invite acceptance page.
      { path: "/invite/:token", element: <AcceptInvite /> },
      // Convenience: Mobile mockup gallery on /mobile (DEV/preview only).
      { path: "/mobile", element: <Mobile /> },
      // Public content pages
      { path: "/about", element: <About /> },
      { path: "/blog", element: <Blog /> },
      { path: "/blog/:slug", element: <BlogPost /> },
      { path: "/contact", element: <Contact /> },
      // Free public utility: Polish surname declension → invitation salutation.
      { path: "/odmiana-nazwisk", element: <SalutationGenerator /> },
      // 301 from the original launch URL — keep any shared links working.
      {
        path: "/zapraszamy-nazwisko",
        element: <Navigate to="/odmiana-nazwisk" replace />,
      },
      // Legal pages — all served by the same LegalPage component via slug
      { path: "/polityka-prywatnosci", element: <LegalPage /> },
      { path: "/regulamin", element: <LegalPage /> },
      { path: "/impressum", element: <LegalPage /> },
      { path: "/rodo", element: <LegalPage /> },
      { path: "/cookies", element: <LegalPage /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);
